// The shell's chat drawer (APP_SHELL.md, CHAT.md): reachable from every
// league screen, never navigates away. Right drawer on tablet and desktop,
// full-height sheet on phone. Tracks unread per league and per device.
//
// It holds THE league's chat feed (`data/chat.ts`), one for the whole shell,
// so the chip's count, the panel and the notify sound read the same messages
// and a real league is asked once, not once per reader.
//
// AND A REAL LEAGUE'S DIRECT CONVERSATIONS (I8, PLAN-2026-10-18 §10.5): the
// drawer shows the league's room, the list of mine, or one of them; the list
// rides the league channel's answers, and a conversation is new while its
// last message is somebody else's and newer than this device last saw it.
// `?chat=<channel>` on a league's address — where a notice of a direct
// message lands — opens the drawer on that channel.

import { SidePanel } from '../kit/overlays';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isDmChannel } from '@fsp/domain';
import { useAccountPrefs } from '../data/accountPrefs';
import {
  startConversation,
  useChatFeed,
  type ChatAttachment,
  type ChatFeed,
  type ChatOutcome,
} from '../data/chat';
import { isDemoLeague } from '../data/demo';
import { useActiveLeague } from '../data/LeaguesProvider';
import { useLeagueSource } from '../data/leagueSource';
import { useSim } from '../data/SimProvider';
import { useDelight } from '../delight/useDelight';
import { ChatPanel } from './ChatPanel';
import { ConversationList, ConversationPanel } from './Conversations';
import { Segments } from '../kit/ui';
import { storageKey } from '../data/storage';
import { placeSequencePending } from './keyboardMap';

/**
 * Per league, the time of the newest message seen with the drawer open; per
 * conversation, under `<league>|<channel>`.
 *
 * A TIME, NOT AN ID (v1 held the id). A real league's drawer holds the newest
 * page, and once the message last read has scrolled off it an id can no
 * longer be found — every message would have counted as unread again.
 */
const READ_KEY = storageKey('chat.read.v2');

function readMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(READ_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

/** What the drawer shows: the league's room, my conversations, or one (`dm:…`). */
export type ChatView = 'league' | 'messages' | `dm:${string}`;

interface ChatDrawerValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** The league's unread lines and the conversations with something new, together. */
  unread: number;
  /** The league's chat, for whatever draws it. */
  feed: ChatFeed;
  view: ChatView;
  setView: (view: ChatView) => void;
  /** The conversations with something new in them, by channel. */
  fresh: ReadonlySet<string>;
  /**
   * Open the conversation with whoever manages `teamId`, beginning it if need
   * be (I8). A real league's alone.
   */
  message: (teamId: string) => Promise<ChatOutcome>;
  /**
   * The name of `teamId`'s manager when they are somebody I can write to — a
   * person, not me — else null. Their name alone: a co-manager is not who
   * Message reaches.
   */
  whoToMessage: (teamId: string) => string | null;
  /**
   * A player or a game going with the next line, said over the box until it
   * is sent or taken off (PLAN-2026-10-31-players-and-games-in-chat).
   */
  attachment: ChatAttachment | null;
  /**
   * Put one with the next line, or take it off with null. From a screen (a
   * player's card, Game Day) it opens the drawer on the league's room, where
   * CHAT.md has a taunt from the matchup land; from the box itself (`open:
   * false`) it stays where the box is, the draft room's or a conversation's.
   * Nothing is sent until the person sends it. A real league's alone;
   * elsewhere false.
   */
  attach: (attachment: ChatAttachment | null, options?: { open?: boolean }) => boolean;
  /** Whether a player or a game can be shared here: a person's real league. */
  canShare: boolean;
}

const ChatDrawerContext = createContext<ChatDrawerValue | null>(null);

export function ChatDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const { activeLeagueId } = useSim();
  const card = useActiveLeague();
  // The league in the URL when it is a real one; the simulator's otherwise.
  const leagueKey = card?.league.id ?? activeLeagueId;
  // ANOTHER LEAGUE IS ANOTHER ROOM: a view belongs to the league it was
  // chosen in, compared in render, so another league's drawer opens on its
  // own channel and never on a conversation of the last one's.
  const [chosen, setChosen] = useState<{ league: string | null; view: ChatView }>({
    league: null,
    view: 'league',
  });
  const view: ChatView = chosen.league === leagueKey ? chosen.view : 'league';
  const setView = useCallback(
    (next: ChatView) => setChosen({ league: leagueKey, view: next }),
    [leagueKey],
  );
  const feed = useChatFeed(open);
  // A MUTED READER IS NOT TOLD OF A BOT'S LINE (B-299): not counted unread,
  // and no sound. A real league's chat leaves them out already; the
  // simulator's is this client's own.
  const hideBots = useAccountPrefs().account?.chat?.hideBots ?? false;
  const messages = useMemo(
    () => (hideBots ? feed.messages.filter((m) => m.kind !== 'ai') : feed.messages),
    [feed.messages, hideBots],
  );
  const { play } = useDelight();
  const [readAt, setReadAt] = useState<Record<string, string>>(readMap);
  const lastHeard = useRef<{ league: string | null; at: string | null }>({
    league: null,
    at: null,
  });
  /** The newest line from somebody else in any conversation, as last heard, per league. */
  const lastHeardDm = useRef<{ league: string | null; at: string | null } | null>(null);
  const lastNotify = useRef(0);

  const newest = messages[messages.length - 1]?.at ?? null;
  const unreadLeague = useMemo(() => {
    if (!leagueKey) return 0;
    const since = readAt[leagueKey];
    return messages.filter((m) => m.kind !== 'system' && (since === undefined || m.at > since))
      .length;
  }, [messages, readAt, leagueKey]);

  const conversations = feed.conversations;
  const fresh = useMemo(() => {
    const out = new Set<string>();
    if (!leagueKey || !conversations) return out;
    for (const c of conversations) {
      if (c.last === null || c.last.mine) continue;
      const seen = readAt[`${leagueKey}|${c.channel}`];
      if (seen === undefined || c.last.at > seen) out.add(c.channel);
    }
    return out;
  }, [conversations, readAt, leagueKey]);

  const markRead = useCallback((key: string, at: string) => {
    setReadAt((r) => {
      if (r[key] !== undefined && r[key] >= at) return r;
      const next = { ...r, [key]: at };
      try {
        localStorage.setItem(READ_KEY, JSON.stringify(next));
      } catch {
        // Storage blocked: unread resets on reload. Acceptable.
      }
      return next;
    });
  }, []);

  // While the league's room is open, everything in it is read.
  useEffect(() => {
    if (!open || view !== 'league' || !leagueKey || !newest) return;
    markRead(leagueKey, newest);
  }, [open, view, leagueKey, newest, markRead]);

  // And while a conversation is open, everything in it.
  const openLast =
    isDmChannel(view) && conversations
      ? (conversations.find((c) => c.channel === view)?.last?.at ?? null)
      : null;
  useEffect(() => {
    if (!open || !leagueKey || openLast === null) return;
    markRead(`${leagueKey}|${view}`, openLast);
  }, [open, view, leagueKey, openLast, markRead]);

  // A new message from someone else while closed: one soft notify per minute.
  // By time rather than by count, since a real league's page is a window that
  // slides rather than a list that grows. A league's first page is heard, not
  // announced.
  useEffect(() => {
    const heard = lastHeard.current;
    lastHeard.current = { league: leagueKey, at: newest };
    if (heard.league !== leagueKey || heard.at === null || open) return;
    const fromOthers = messages.some(
      (m) => m.at > heard.at! && (m.kind === 'user' || m.kind === 'ai'),
    );
    if (fromOthers && Date.now() - lastNotify.current > 60_000) {
      lastNotify.current = Date.now();
      play('notify');
    }
  }, [messages, newest, leagueKey, open, play]);

  // The same for a conversation, unless it is the one open.
  const newestDm = useMemo(() => {
    let at: { at: string; channel: string } | null = null;
    for (const c of conversations ?? [])
      if (c.last !== null && !c.last.mine && (at === null || c.last.at > at.at))
        at = { at: c.last.at, channel: c.channel };
    return at;
  }, [conversations]);
  // A league's first list is heard, not announced; after that the mark only
  // moves forward, so a reply of mine in between never silences the next line.
  useEffect(() => {
    if (conversations === undefined) return;
    const heard = lastHeardDm.current;
    const at = newestDm?.at ?? null;
    if (heard === null || heard.league !== leagueKey) {
      lastHeardDm.current = { league: leagueKey, at };
      return;
    }
    if (newestDm === null || (heard.at !== null && newestDm.at <= heard.at)) return;
    lastHeardDm.current = { league: leagueKey, at: newestDm.at };
    if (open && view === newestDm.channel) return;
    if (Date.now() - lastNotify.current > 60_000) {
      lastNotify.current = Date.now();
      play('notify');
    }
  }, [conversations, newestDm, leagueKey, open, view, play]);

  // `c` toggles the drawer on desktop, outside inputs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'c' || e.metaKey || e.ctrlKey || e.altKey) return;
      // `g c` is the Calendar (`keyboardMap`), not chat.
      if (e.defaultPrevented || placeSequencePending()) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (!leagueKey) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [leagueKey]);

  // `?chat=<channel>` OPENS THE DRAWER ON IT, and is taken off the address so
  // a reload or the way back does not open it again. A conversation waits for
  // the real league to be known, so it is chosen in that league and not in
  // whatever answered first.
  const location = useLocation();
  const navigate = useNavigate();
  const realKey = card?.league.id ?? null;
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const asked = params.get('chat');
    if (asked === null || !leagueKey) return;
    if (isDmChannel(asked) && realKey === null) return;
    params.delete('chat');
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
    if (asked === 'league' || isDmChannel(asked)) {
      setChosen({ league: leagueKey, view: asked as ChatView });
      setOpen(true);
    }
  }, [location.search, location.pathname, leagueKey, realKey, navigate]);

  const source = useLeagueSource();
  // A PERSON'S LEAGUE ALONE: a practice league has nobody in it, and the demo
  // league's visitor is nobody's to write as.
  const realLeagueId =
    source === 'api' && card !== null && !isDemoLeague(card.league.id) ? card.league.id : null;
  const message = useCallback(
    async (teamId: string): Promise<ChatOutcome> => {
      if (realLeagueId === null)
        return { ok: false, message: 'Messages are for a league with people in it.' };
      const started = await startConversation(realLeagueId, teamId);
      if (!started.ok) return started;
      setChosen({ league: realLeagueId, view: started.conversation.channel as ChatView });
      setOpen(true);
      return { ok: true };
    },
    [realLeagueId],
  );
  const people = realLeagueId === null ? undefined : feed.people;
  const whoToMessage = useCallback(
    (teamId: string) => people?.find((p) => p.teamId === teamId)?.name ?? null,
    [people],
  );

  // AN ATTACHMENT BELONGS TO ITS LEAGUE, compared in render as the view is:
  // another league's box never offers this one's player.
  const [held, setHeld] = useState<{ league: string; attachment: ChatAttachment } | null>(null);
  const attachment = held !== null && held.league === realLeagueId ? held.attachment : null;
  const attach = useCallback(
    (next: ChatAttachment | null, options?: { open?: boolean }): boolean => {
      if (realLeagueId === null) return false;
      setHeld(next === null ? null : { league: realLeagueId, attachment: next });
      if (next !== null && options?.open !== false) {
        setView('league');
        setOpen(true);
      }
      return true;
    },
    [realLeagueId, setView],
  );
  const canShare = realLeagueId !== null;

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const unread = unreadLeague + fresh.size;
  const value = useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      unread,
      feed,
      view,
      setView,
      fresh,
      message,
      whoToMessage,
      attachment,
      attach,
      canShare,
    }),
    [
      open,
      toggle,
      unread,
      feed,
      view,
      setView,
      fresh,
      message,
      whoToMessage,
      attachment,
      attach,
      canShare,
    ],
  );
  return <ChatDrawerContext.Provider value={value}>{children}</ChatDrawerContext.Provider>;
}

export function useChatDrawer(): ChatDrawerValue {
  const ctx = useContext(ChatDrawerContext);
  if (!ctx) throw new Error('useChatDrawer must be used inside ChatDrawerProvider');
  return ctx;
}

/** The drawer, or null where a screen is drawn without one (a lab page, a test). */
export function useChatDrawerIfAny(): ChatDrawerValue | null {
  return useContext(ChatDrawerContext);
}

type Room = 'league' | 'messages';

export function ChatDrawer() {
  const { open, setOpen, view, setView, fresh } = useChatDrawer();
  const { state, activeLeagueId } = useSim();
  const card = useActiveLeague();
  const name = card?.league.name ?? state.leagueName;
  // A PRACTICE LEAGUE HAS NOBODY TO WRITE TO, and the demo's visitor is
  // nobody to write as: their drawer is the room alone.
  const withMessages =
    useLeagueSource() === 'api' && card !== null && !isDemoLeague(card.league.id);
  const room: Room = view === 'league' ? 'league' : 'messages';
  const rooms = useMemo(
    () => [
      { id: 'league' as const, label: 'League' },
      { id: 'messages' as const, label: 'Messages', count: fresh.size, tone: 'accent' as const },
    ],
    [fresh.size],
  );
  return (
    // The screen behind stays readable (CHAT.md: it never navigates away).
    <SidePanel
      opened={open && (card !== null || activeLeagueId !== null)}
      onClose={() => setOpen(false)}
      title={`${name} chat`}
      closeLabel="Close chat"
    >
      {withMessages ? (
        <div className="fsp-chat-drawer">
          <div className="fsp-chat-rooms">
            <Segments label="Chat" items={rooms} value={room} onChange={(r) => setView(r)} />
          </div>
          <div className="fsp-chat-room">
            {view === 'league' ? (
              <ChatPanel />
            ) : view === 'messages' ? (
              <ConversationList />
            ) : (
              // One conversation's draft and keys are never another's.
              <ConversationPanel key={view} channel={view} />
            )}
          </div>
        </div>
      ) : (
        <ChatPanel />
      )}
    </SidePanel>
  );
}
